import { PlaceAddressComponent } from '../../../../../types';
import { AddressFormValues } from '../types';

export type MappedPlace = Partial<AddressFormValues> & {
  // ISO alpha-2 from the `country` component; informational only — the
  // payload always carries the brand's configured country.
  country?: string;
};

const pick = (
  components: PlaceAddressComponent[],
  type: string,
): PlaceAddressComponent | undefined => {
  return components.find(component => {
    return component.types.includes(type);
  });
};

const firstOf = (
  components: PlaceAddressComponent[],
  types: string[],
): string => {
  for (const type of types) {
    const match = pick(components, type);
    if (match?.longText) {
      return match.longText;
    }
  }
  return '';
};

/**
 * Map Place Details (New) `addressComponents` onto the VoWiFi address form.
 *
 * Components are typed semantically — never positionally — so locales where
 * the house number follows the street name still map correctly. A result
 * without `street_number` (route / POI) leaves the civic number EMPTY rather
 * than guessing; `formattedAddress` is never parsed.
 */
export const mapPlaceDetails = (
  components: PlaceAddressComponent[] = [],
): MappedPlace => {
  const streetNumber = pick(components, 'street_number')?.longText ?? '';
  const route = pick(components, 'route')?.longText ?? '';
  const subpremise = pick(components, 'subpremise')?.longText ?? '';
  /* City preference (verified against the live APIs, 2026-09-02):
   * - `sublocality` BEFORE `locality`: Google labels amalgamated communities
   *   as sublocality with the parent municipality as locality (e.g. Vanier
   *   inside Ottawa) — the community is what its own prediction text shows
   *   and Address Validation confirms it as the locality, so it wins.
   * - admin_area_level_3/2 as last resorts: rural results can carry NO
   *   locality-class component at all (e.g. Van Norden Rd, Tusket NS only
   *   has administrative_area_level_2 'Argyle') — Google's own name for the
   *   area beats leaving the field empty. */
  const city = firstOf(components, [
    'sublocality',
    'locality',
    'postal_town',
    'administrative_area_level_3',
    'administrative_area_level_2',
  ]);
  const region =
    pick(components, 'administrative_area_level_1')?.shortText ?? '';
  const postal = pick(components, 'postal_code')?.longText ?? '';
  const country = pick(components, 'country')?.shortText;

  return {
    street_number: streetNumber,
    address_line_1: route,
    address_line_2: subpremise,
    city,
    state_province_region: region,
    zip_postal_code: postal,
    ...(country ? { country } : {}),
  };
};
